import * as React from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Stack from '@mui/material/Stack';
import MenuIcon from '@mui/icons-material/Menu';
import BottomBar from './components/BottomBar';
import MobileMenu from './components/MobileMenu';
import TopBar from './components/TopBar';
import { HeaderProps } from './Interfaces';
import { useBreakpoint } from '../utils/useBreakpoint';
import Container from '../common/Container/Container';
import Spacings from '../theme/common/spacings';
import styles from './Header.styles';

const Header: React.FC<HeaderProps> = ({
  menuElements,
  isLoggedIn,
  accountLinks,
  logo,
  notifications,
  messages,
  mobileAccountLinks,
  buttonsSettings,
  additionalContent,
  hideLoginRegister,
  hideMobileMenu,
  sx = {},
}: HeaderProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const smDownBreakpoint = useBreakpoint('smDown');
  const smUpBreakpoint = useBreakpoint('smUp');

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const headerLogoHeight = 32;
  const centeredHeaderLogo: React.JSX.Element = (
    <Stack alignItems="center">
      <Box height={headerLogoHeight}>
        {logo.external ? (
          <a href={logo.url}>
            <img src={logo.image} alt="logo" height={headerLogoHeight} />
          </a>
        ) : (
          <Link to={logo.url}>
            <img src={logo.image} alt="logo" height={headerLogoHeight} />
          </Link>
        )}
      </Box>
    </Stack>
  );

  return (
    <AppBar
      elevation={0}
      sx={{
        height: 'auto',
        position: 'sticky',
        pr: '0px !important',
        background: 'white',
        boxShadow: '0 1px 4px #19316f60',
        '& .MuiButtonBase-root:focus': {
          outline: 'unset',
        },
        '& #registerButton': {
          color: 'danger.40',
          borderColor: 'danger.40',
          textDecoration: 'none',
          background: 'common.white',
          '&:hover': {
            color: 'common.white',
            backgroundColor: 'danger.30',
            borderColor: 'danger.30',
            textDecoration: 'none',
          },
        },
        '& #loginButton': {
          marginLeft: Spacings.small3,
          color: 'common.white',
          backgroundColor: 'danger.40',
          '&:hover': {
            color: 'common.white',
            backgroundColor: 'danger.30',
            textDecoration: 'none',
          },
        },
        ...sx,
      }}
    >
      <Container>
        <Toolbar sx={styles.toolbar}>
          {centeredHeaderLogo}

          <Box sx={{ flexGrow: 1 }}>{additionalContent}</Box>

          <TopBar
            isLoggedIn={isLoggedIn}
            notifications={notifications}
            messages={messages}
            accountLinks={accountLinks}
            buttonsSettings={buttonsSettings}
            hideLoginRegister={hideLoginRegister}
          />
          {smDownBreakpoint && !hideMobileMenu && (
            <>
              <Divider
                aria-hidden="true"
                orientation="vertical"
                sx={styles.mobileMenuItemDivider}
              />
              <IconButton
                size="small"
                color="ghostGray"
                onClick={toggleDrawer}
                sx={{ marginLeft: Spacings.small3 }}
              >
                <MenuIcon />
              </IconButton>
            </>
          )}
        </Toolbar>
        <Divider sx={styles.toolbarDivider} />
        {isLoggedIn && smUpBreakpoint && (
          <BottomBar menuElements={menuElements} />
        )}
        {smDownBreakpoint && (
          <MobileMenu
            isDrawerOpen={isDrawerOpen}
            toggleDrawer={toggleDrawer}
            menuElements={menuElements}
            mobileAccountLinks={mobileAccountLinks}
            isLoggedIn={isLoggedIn}
            buttonsSettings={buttonsSettings}
          />
        )}
      </Container>
    </AppBar>
  );
};

export default Header;
